import { useEffect, useId, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { confirmDataRequest, dataExportUrl, type DataConfirmation } from "../../lib/api";
import { formatDate } from "../../lib/format";
import { dataRequestsQuery, qk } from "../../lib/queries";
import { LinkButton, SecondaryButton, secondaryClass } from "../ui/button";

/** What a screen reader calls the strip. */
export const DATA_REQUESTS_LABEL = "Requests about your data";

export const ERASURE_TITLE = "Confirm you asked us to delete your account";
export const EXPORT_TITLE = "Confirm you asked us for a copy of your data";
export const CONFIRM_LABEL = "Yes, I asked for this";
export const CONFIRMED_ERASURE = "Your deletion request is confirmed.";
export const CONFIRMED_EXPORT =
  "Your request for a copy of your data is confirmed — we'll prepare it.";
export const CONFIRM_FAILED = "That did not go through. Try again, or write to support@nob.me.";
export const EXPORT_READY_TITLE = "Your data export is ready";
export const DOWNLOAD_LABEL = "Download";
export const DISMISS_LABEL = "Dismiss";

/** What was asked, and what happens on a click — in plain words, with the lapse date. */
export function confirmationLine(c: DataConfirmation): string {
  const until = formatDate(c.expiresAt);
  const lapses = until ? ` This lapses on ${until}.` : "";
  return c.kind === "erasure"
    ? `We received a request to delete your Nob account and what is on it. If that was you, confirm it here. If it was not you, do nothing — and tell us at support@nob.me.${lapses}`
    : `We received a request for a copy of everything we hold about your account. If that was you, confirm it here and we will prepare it. If it was not you, do nothing: nothing is done.${lapses}`;
}

export function exportReadyLine(expiresAt: string): string {
  const until = formatDate(expiresAt);
  return until
    ? `A copy of what we hold about your account. Download it by ${until}; after that it is deleted.`
    : "A copy of what we hold about your account. It is deleted a week after it was made.";
}

/** One line once the file has been opened: where it is, and when it goes. */
export function exportDownloadedLine(expiresAt: string): string {
  const until = formatDate(expiresAt);
  return until
    ? `Your data export was downloaded. It stays here until ${until}, then it is deleted.`
    : "Your data export was downloaded. It is deleted a week after it was made.";
}

const CARD =
  "flex flex-col gap-3 rounded-lg border border-border-strong bg-bg px-4 py-3 " +
  "sm:flex-row sm:items-center sm:justify-between";

/** One confirmation: a card with its title, its words and its one button. */
function ConfirmationCard({
  c,
  busy,
  failed,
  onConfirm,
}: {
  c: DataConfirmation;
  busy: boolean;
  failed: boolean;
  onConfirm: () => void;
}) {
  const titleId = useId();
  return (
    <section aria-labelledby={titleId} data-data-request={c.kind} className={CARD}>
      <div className="min-w-0 flex-1">
        <p id={titleId} className="text-sm font-medium text-text">
          {c.kind === "erasure" ? ERASURE_TITLE : EXPORT_TITLE}
        </p>
        <p className="mt-1 text-[13px] leading-snug text-text-soft">{confirmationLine(c)}</p>
        {failed && (
          <p role="status" className="mt-1 text-[13px] leading-snug text-danger">
            {CONFIRM_FAILED}
          </p>
        )}
      </div>
      <SecondaryButton
        className="w-full sm:w-auto"
        aria-describedby={titleId}
        disabled={busy}
        onClick={onConfirm}
      >
        {CONFIRM_LABEL}
      </SecondaryButton>
    </section>
  );
}

/**
 * **A REQUEST ABOUT THE PERSON'S DATA, IN THE CONSOLE** (wave 116; owner,
 * 2026-10-02). An erasure or an export is carried out only for somebody who
 * proved they asked, and one way to prove it is to be signed in and say so: a
 * member of staff asks the backoffice for the confirmation, and it shows up
 * here, over everything, with one button. The other half is the file itself —
 * the export is delivered here and nowhere else, for seven days.
 *
 * Nothing the staff member typed is shown, and nothing is shown about money.
 * It draws nothing when there is nothing to say, which is every day of almost
 * every account's life. A confirmed request is replaced, in its own place, by
 * one line naming what was confirmed, and focus moves to it. The strip is
 * capped, so several notices never squeeze the app on a phone.
 */
export function DataRequestsNotice() {
  const queryClient = useQueryClient();
  const { data } = useQuery(dataRequestsQuery());
  const [confirmed, setConfirmed] = useState<ReadonlyMap<string, DataConfirmation["kind"]>>(
    new Map(),
  );
  const [dismissed, setDismissed] = useState<ReadonlySet<string>>(new Set());
  const [busy, setBusy] = useState<string | null>(null);
  const [failed, setFailed] = useState<string | null>(null);
  // Where focus goes after a click: the confirmed line that replaced the card.
  const [focusId, setFocusId] = useState<string | null>(null);
  const lineRefs = useRef(new Map<string, HTMLParagraphElement>());

  useEffect(() => {
    if (focusId === null) return;
    lineRefs.current.get(focusId)?.focus();
    setFocusId(null);
  }, [focusId, confirmed]);

  if (!data) return null;
  // A confirmation is a card until clicked, then its line, in the same place.
  const shown = data.confirmations;
  const exports = data.exports.filter((e) => !dismissed.has(e.id));
  if (shown.length === 0 && exports.length === 0 && confirmed.size === 0) return null;

  const confirm = async (c: DataConfirmation) => {
    setBusy(c.id);
    setFailed(null);
    const result = await confirmDataRequest(c.id);
    setBusy(null);
    if (result.ok) {
      setConfirmed((prev) => new Map(prev).set(c.id, c.kind));
      setFocusId(c.id);
      void queryClient.invalidateQueries({ queryKey: qk.dataRequests() });
    } else {
      setFailed(c.id);
    }
  };

  // The confirmations the person has clicked disappear from the server's list
  // on the next read; their lines are drawn from `confirmed`, in order.
  const lines = [...confirmed.entries()].filter(
    ([id]) => !data.confirmations.some((c) => c.id === id),
  );

  return (
    <section
      aria-label={DATA_REQUESTS_LABEL}
      data-data-requests
      className="flex max-h-[40dvh] shrink-0 flex-col gap-2 overflow-y-auto border-b border-border-soft bg-surface px-4 py-3"
    >
      {shown.map((c) =>
        confirmed.has(c.id) ? (
          <ConfirmedLine key={c.id} id={c.id} kind={c.kind} refs={lineRefs.current} />
        ) : (
          <ConfirmationCard
            key={c.id}
            c={c}
            busy={busy === c.id}
            failed={failed === c.id}
            onConfirm={() => void confirm(c)}
          />
        ),
      )}
      {lines.map(([id, kind]) => (
        <ConfirmedLine key={id} id={id} kind={kind} refs={lineRefs.current} />
      ))}
      {exports.map((e) =>
        e.downloadedAt ? (
          <div
            key={e.id}
            data-data-export={e.id}
            className="flex items-center justify-between gap-3 text-[13px] text-text-soft"
          >
            <p>{exportDownloadedLine(e.expiresAt)}</p>
            <div className="flex shrink-0 gap-3">
              <a className="underline underline-offset-4" href={dataExportUrl(e.id)} download>
                {DOWNLOAD_LABEL}
              </a>
              <LinkButton onClick={() => setDismissed((p) => new Set(p).add(e.id))}>
                {DISMISS_LABEL}
              </LinkButton>
            </div>
          </div>
        ) : (
          <ExportCard
            key={e.id}
            id={e.id}
            expiresAt={e.expiresAt}
            onDownloaded={() =>
              // The api records the first read; read the list again so the card
              // collapses to its one line.
              setTimeout(
                () => void queryClient.invalidateQueries({ queryKey: qk.dataRequests() }),
                800,
              )
            }
            onDismiss={() => setDismissed((p) => new Set(p).add(e.id))}
          />
        ),
      )}
    </section>
  );
}

function ConfirmedLine({
  id,
  kind,
  refs,
}: {
  id: string;
  kind: DataConfirmation["kind"];
  refs: Map<string, HTMLParagraphElement>;
}) {
  return (
    <p
      ref={(el) => {
        if (el) refs.set(id, el);
        else refs.delete(id);
      }}
      role="status"
      tabIndex={-1}
      data-data-request-confirmed={kind}
      className="rounded-lg border border-border-soft bg-bg px-4 py-3 text-[13px] text-text-soft focus:outline-none"
    >
      {kind === "erasure" ? CONFIRMED_ERASURE : CONFIRMED_EXPORT}
    </p>
  );
}

function ExportCard({
  id,
  expiresAt,
  onDownloaded,
  onDismiss,
}: {
  id: string;
  expiresAt: string;
  onDownloaded: () => void;
  onDismiss: () => void;
}) {
  const titleId = useId();
  return (
    <section aria-labelledby={titleId} data-data-export={id} className={CARD}>
      <div className="min-w-0 flex-1">
        <p id={titleId} className="text-sm font-medium text-text">
          {EXPORT_READY_TITLE}
        </p>
        <p className="mt-1 text-[13px] leading-snug text-text-soft">{exportReadyLine(expiresAt)}</p>
      </div>
      <div className="flex flex-col items-stretch gap-2 sm:flex-row sm:items-center">
        <a
          className={`${secondaryClass} w-full sm:w-auto`}
          href={dataExportUrl(id)}
          download
          aria-describedby={titleId}
          onClick={onDownloaded}
        >
          {DOWNLOAD_LABEL}
        </a>
        <LinkButton onClick={onDismiss}>{DISMISS_LABEL}</LinkButton>
      </div>
    </section>
  );
}
